/**
 * Helper functions for Focus Features navigation conditional logic
 */

import { isFocusStudio } from 'utils/studioConfig';

/**
 * Check if Videos navigation item should be shown
 * For Universal: Videos should appear when the videos page is active (toggled on)
 * For Focus Features: Videos should appear only when videos are added in Media tab
 * @param {Object} movieData - Movie content data
 * @returns {boolean}
 */
export function shouldShowVideos(movieData) {
	// For Focus Features: Only check if videos exist
	if (isFocusStudio()) {
		const trailers = movieData?.trailers || {};
		return Object.values(trailers).some(t => t?.url);
	}

	// For Universal Studios: Show videos if the page is active
	return movieData?.pages?.videos?.active || false;
}

/**
 * Check if Tickets navigation item should be shown
 * Tickets should be shown only when ticketing date has been reached and page is active
 * @param {Object} movieData - Movie content data
 * @returns {boolean}
 */
export function shouldShowTickets(movieData) {
	// Check status.theaters which validates both page active AND date reached
	const status = movieData?.status;
	const showtimesAvailable = status?.theaters === 'preSale' || status?.theaters === 'inTheaters';
	return showtimesAvailable;
}

/**
 * Check if Watch at Home navigation item should be shown
 * Watch at Home should be shown only when HomeEnt date has been reached and page is active
 * @param {Object} movieData - Movie content data
 * @returns {boolean}
 */
export function shouldShowWatchAtHome(movieData) {
	// Check status.homeEnt which validates both page active AND date reached
	const status = movieData?.status;
	const shouldShow = status?.homeEnt === 'homeEnt';
	return shouldShow;
}

/**
 * Check if a navigation link should be displayed (works for both Focus Features and Universal Studios)
 * @param {Object} link - Navigation link object
 * @param {Object} movieData - Movie content data
 * @returns {boolean}
 */
export function shouldShowNavigationLink(link, movieData) {
	switch (link.id) {
		case 'videos':
		case 'boltVideos':
			return shouldShowVideos(movieData);
		case 'showtimes':
		case 'tickets':
		case 'boltShowtimes':
			return shouldShowTickets(movieData);
		case 'watchathome':
		case 'homeEnt': // Universal Studios uses 'homeEnt' instead of 'watchathome'
		case 'boltHomeEnt':
			return shouldShowWatchAtHome(movieData);
		case 'synopsis':
		case 'boltSynopsis':
			return movieData?.pages?.synopsis?.active ?? false;
		case 'gallery':
		case 'boltGallery':
			return movieData?.pages?.gallery?.active ?? false;
		case 'home':
		case 'boltHome':
		default:
			return true;
	}
}
